import React, { useState } from "react";
import { Linkedin, Facebook, Twitter, Mail, Link as LinkIcon, Check, Share2 } from "lucide-react";

export default function ShareButtons({ title, excerpt }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const text = `${title} — ${excerpt || ""}`.trim();

  const enc = encodeURIComponent;
  const links = {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    twitter: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}`,
    whatsapp: `https://wa.me/?text=${enc(text + " " + url)}`,
    email: `mailto:?subject=${enc(title)}&body=${enc((excerpt || "") + "\n\n" + url)}`,
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const buttons = [
    { id: "linkedin", label: "LinkedIn", icon: Linkedin, href: links.linkedin, color: "hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2]" },
    { id: "facebook", label: "Facebook", icon: Facebook, href: links.facebook, color: "hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2]" },
    { id: "twitter", label: "X / Twitter", icon: Twitter, href: links.twitter, color: "hover:bg-black hover:text-white hover:border-black" },
    { id: "whatsapp", label: "WhatsApp", icon: WhatsAppIcon, href: links.whatsapp, color: "hover:bg-[#25D366] hover:text-white hover:border-[#25D366]" },
    { id: "email", label: "Email", icon: Mail, href: links.email, color: "hover:bg-md-primary hover:text-white hover:border-md-primary" },
  ];

  return (
    <div data-testid="share-buttons" className="my-10 p-5 bg-md-bg border border-md-border rounded-2xl">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-2 text-md-primary">
          <Share2 className="w-4 h-4 text-md-secondary" />
          <span className="text-xs uppercase tracking-[0.2em] font-bold">Partager cet article</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          {buttons.map((b) => {
            const I = b.icon;
            return (
              <a
                key={b.id}
                href={b.href}
                target="_blank"
                rel="noreferrer noopener"
                data-testid={`share-${b.id}`}
                aria-label={`Partager sur ${b.label}`}
                title={b.label}
                className={`w-10 h-10 rounded-full grid place-items-center bg-white border border-md-border text-md-primary transition-all ${b.color}`}
              >
                <I className="w-4 h-4" />
              </a>
            );
          })}
          <button
            type="button"
            onClick={copyLink}
            data-testid="share-copy"
            aria-label="Copier le lien"
            title={copied ? "Lien copié !" : "Copier le lien"}
            className={`w-10 h-10 rounded-full grid place-items-center border transition-all ${
              copied
                ? "bg-emerald-500 border-emerald-500 text-white"
                : "bg-white border-md-border text-md-primary hover:bg-md-secondary hover:text-white hover:border-md-secondary"
            }`}
          >
            {copied ? <Check className="w-4 h-4" /> : <LinkIcon className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

function WhatsAppIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}
